// components/InvitationPolls.js
//
// The polls on an invitation, answerable.
//
// They already rendered — question, options, the lot — but every card called
// `openAppOnly('polls')`, so tapping one asked you to download the app. A poll
// nobody can answer is decoration, and the create form on this site can now
// make them, so the loop was open at both ends.
//
// Everything needed was already there: `events.polls` carries the questions,
// `event_poll_responses` takes the answers, and the app counts by option
// index. See services/pollService.js for why the stored shape is indices.
//
// ── Signed out ─────────────────────────────────────────────────────────────
//
// A response row has a `user_id`, so voting needs an account. Rather than hide
// the poll, it shows with the tally and the vote buttons ask for sign-in —
// the same shape as the RSVP flow above it on the same page.

import React, { useState, useEffect, useCallback } from 'react';
import { IoBarChartOutline, IoCheckmarkCircle } from 'react-icons/io5';
import { submitPollResponse, getPollResults } from '../services/pollService';
import './InvitationPolls.scss';

export default function InvitationPolls({
  polls,
  eventId,
  financialYear,
  userId,
  onRequireSignIn,
  fontStyle,
}) {
  const [results, setResults] = useState({ byPoll: {}, mine: {} });
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const data = await getPollResults(eventId, financialYear, polls, userId);
    setResults(data);
  }, [eventId, financialYear, polls, userId]);

  useEffect(() => { load(); }, [load]);

  const vote = async (poll, index) => {
    if (!userId) { onRequireSignIn?.(); return; }

    setBusy(poll.id);
    setError(null);

    const previous = results;

    // Shown as voted before the write lands. A tap on a poll option should
    // not sit there looking ignored while a round trip happens; the previous
    // state is kept so a failure can put it back.
    setResults((current) => ({
      ...current,
      mine: { ...current.mine, [poll.id]: [index] },
    }));

    try {
      await submitPollResponse(eventId, financialYear, poll.id, [index]);
      await load();
    } catch (err) {
      setResults(previous);
      setError(err.message || 'Could not save your vote.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="ipoll-list">
      {polls.map((poll, pollIndex) => {
        const tally = results.byPoll[poll.id] ?? { counts: [], total: 0 };
        const myVotes = results.mine[poll.id] ?? [];
        const voted = myVotes.length > 0;

        return (
          <div className="ipoll" key={poll.id || pollIndex}>
            <p className="ipoll-question" style={fontStyle}>
              {poll.question || poll.title}
            </p>

            {(poll.options || []).map((option, index) => {
              const label = option?.text || option?.label || option;
              const count = tally.counts[index] ?? 0;
              // Percentages only once there is something to be a percentage
              // of; "0% (0 votes)" on an unanswered poll reads as a failure.
              const percent = tally.total > 0 ? Math.round((count / tally.total) * 100) : 0;
              const mine = myVotes.includes(index);

              return (
                <button
                  key={option?.id || index}
                  type="button"
                  className={`ipoll-option ${mine ? 'is-mine' : ''} ${voted ? 'is-voted' : ''}`}
                  onClick={() => vote(poll, index)}
                  disabled={busy === poll.id}
                  aria-pressed={mine}
                >
                  {/* The bar is a sibling behind the label rather than a
                      background on it, so the text never sits on a moving
                      gradient and stays readable at every width. */}
                  {voted && (
                    <span className="ipoll-bar" style={{ width: `${percent}%` }} aria-hidden="true" />
                  )}

                  <span className="ipoll-option-label" style={fontStyle}>
                    {mine && <IoCheckmarkCircle className="ipoll-tick" />}
                    {label}
                  </span>

                  {voted && (
                    <span className="ipoll-count">
                      {percent}%
                    </span>
                  )}
                </button>
              );
            })}

            <p className="ipoll-foot">
              <IoBarChartOutline />
              {tally.total === 0
                ? 'No votes yet'
                : `${tally.total} ${tally.total === 1 ? 'vote' : 'votes'}`}
              {voted && ' · tap another option to change yours'}
              {!userId && ' · sign in to vote'}
            </p>
          </div>
        );
      })}

      {error && <p className="ipoll-error">{error}</p>}
    </div>
  );
}
